import { UserActionWidgetActionQuery$data } from '../../../../graphQLOperations/__generated__/UserActionWidgetActionQuery.graphql';
import { DiagnosticWidgetType } from '../TeacherDiagnosticWidget';
import { WidgetCompleted, widgetInitialState } from '../TeacherDiagnosticWidgetWrapper';

/** "Teacher hid the diagnostic widget" user action. */
export const HIDE_DIAGNOSTIC_WIDGET_ACTION_TYPE_ID = 'ActionTypeNode:22';

/** Classes the diagnostic widget has already been hidden for. */
export const getHiddenDiagnosticWidgetClassIds = (
  allUserActions: UserActionWidgetActionQuery$data['allUserActions'],
): Set<string> =>
  new Set(
    (allUserActions?.nodes ?? [])
      .filter(el => el.actionType?.id === HIDE_DIAGNOSTIC_WIDGET_ACTION_TYPE_ID)
      .map(el => el.class?.id)
      .filter((id): id is string => !!id),
  );

const widgetActionMap: Record<string, DiagnosticWidgetType> = {
  'ActionTypeNode:17': DiagnosticWidgetType.REVIEW_CLASSES,
  'ActionTypeNode:18': DiagnosticWidgetType.PREPARE,
  'ActionTypeNode:19': DiagnosticWidgetType.UNDERSTAND_STUDENT_EXPERIENCE,
  'ActionTypeNode:20': DiagnosticWidgetType.CLASS_FOCUS,
};

interface WidgetState {
  dismissGuide: boolean;
  widgetCompleted?: WidgetCompleted[];
}

export const buildWidgetState = (
  allUserActions: UserActionWidgetActionQuery$data['allUserActions'],
  hasUserClassWithActiveStudent?: boolean | null,
  anyStudentCompletedDiagnostic?: boolean,
  classFocusCompleted?: boolean,
  // Tasks completed during this session. The user-actions query is fetched once on mount and is
  // never refetched, so a task the teacher just ticked is absent from `allUserActions` — without
  // this set it would be rebuilt as unchecked every time a dependency (e.g. the class filter)
  // makes the caller recompute.
  locallyCompleted?: ReadonlySet<DiagnosticWidgetType>,
): WidgetState => {
  const widgetDismissed = allUserActions?.nodes?.find(el => el.actionType?.id === 'ActionTypeNode:21');

  if (widgetDismissed) {
    return { dismissGuide: true };
  }

  const completedWidgets = new Set(
    allUserActions?.nodes
      ?.map(el => (el.actionType?.id ? widgetActionMap[el.actionType.id] : undefined))
      .filter((widget): widget is DiagnosticWidgetType => widget !== undefined),
  );

  const widgetCompleted = widgetInitialState.map(widget => {
    const isReviewClasses = widget.name === DiagnosticWidgetType.REVIEW_CLASSES;
    const isClassFocus = widget.name === DiagnosticWidgetType.CLASS_FOCUS;

    // Class focus is auto-completed by the focus flow; the rest are marked from user actions
    // (persisted ones from the query, this session's ones from `locallyCompleted`).
    const checked =
      (isClassFocus && Boolean(classFocusCompleted)) ||
      completedWidgets.has(widget.name) ||
      Boolean(locallyCompleted?.has(widget.name));

    // A completed task is never blocked. Otherwise, review-classes waits for an active student and
    // class-focus waits for a submitted diagnostic — the Unit Progress widget renders in every view
    // (with or without a class in scope), so the filter never blocks the task.
    const blocked =
      !checked &&
      ((isReviewClasses && !hasUserClassWithActiveStudent) || (isClassFocus && !anyStudentCompletedDiagnostic));

    return { name: widget.name, checked, blocked };
  });

  return { dismissGuide: false, widgetCompleted };
};
